import { useState } from 'react'
import DopaLogo from './DopaLogo'

interface GentleBotPromptProps {
  onAccept: () => void
  onIgnore: () => void
  isVoiceCue?: boolean
  candleCount?: number
}

const GentleBotPrompt = ({ onAccept, onIgnore, isVoiceCue = false, candleCount = 1 }: GentleBotPromptProps) => {
  const [isVisible, setIsVisible] = useState(true)

  const handleIgnore = () => {
    setIsVisible(false)
    setTimeout(() => {
      onIgnore()
    }, 300)
  }

  const getMessage = () => {
    if (isVoiceCue && candleCount >= 3) {
      return {
        title: "A gentle voice",
        message: "I've been here quietly with you. Sometimes talking can help when we're ready. Would you like to share what's on your heart?",
        acceptText: "Talk to Dopa",
        ignoreText: "More quiet time"
      }
    } else if (isVoiceCue) {
      return {
        title: "Soft presence",
        message: "I'm here if you'd like some gentle company. No pressure at all.",
        acceptText: "I'd like to talk",
        ignoreText: "Light another candle"
      }
    } else {
      return {
        title: "Gentle companion nearby",
        message: "Sometimes solitude helps us heal. Sometimes the right company does. I'm here if you'd like to talk, or you can continue in your quiet space.",
        acceptText: "Talk to Dopa",
        ignoreText: "Stay in quiet"
      }
    }
  }

  const content = getMessage()

  if (!isVisible) return null

  return (
    <div 
      className="fixed bottom-4 sm:bottom-8 left-4 sm:left-8 right-4 sm:right-8 z-20 transition-all duration-300"
      style={{ 
        animation: isVoiceCue ? 'gentlePulse 2s ease-in-out infinite' : 'slideUp 0.5s ease-out'
      }}
    >
      <div className="bg-peach-50/30 dark:bg-peach-900/30 backdrop-blur-sm rounded-3xl border border-peach-100/30 dark:border-peach-700/30 p-4 sm:p-6 max-w-sm mx-auto shadow-lg">
        <div className="text-center mb-4 sm:mb-6">
          <div className="mx-auto mb-3 sm:mb-4">
            <DopaLogo size="sm" className="justify-center" withText={false} />
          </div>
          
          <h3 className="text-peach-600 dark:text-peach-300 font-light text-sm sm:text-base mb-2 sm:mb-3">
            {content.title}
          </h3>
          
          <p className="text-peach-500 dark:text-peach-400 font-light text-xs sm:text-sm leading-relaxed">
            {content.message}
          </p>
        </div>

        <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-3">
          <button
            onClick={onAccept}
            className="flex-1 px-3 sm:px-4 py-2 sm:py-3 bg-peach-100/40 hover:bg-peach-200/40 
                       dark:bg-peach-800/40 dark:hover:bg-peach-700/40
                       text-peach-600 dark:text-peach-300 rounded-2xl text-xs sm:text-sm font-light border border-peach-200/30 dark:border-peach-600/30 hover:border-peach-300/40 dark:hover:border-peach-500/40
                       transition-colors duration-300 focus:outline-none focus:ring-4 focus:ring-peach-100/30 dark:focus:ring-peach-700/30"
          >
            {content.acceptText}
          </button>
          
          <button
            onClick={handleIgnore}
            className="flex-1 px-3 sm:px-4 py-2 sm:py-3 bg-peach-50/20 dark:bg-peach-900/20 hover:bg-peach-100/30 dark:hover:bg-peach-800/30 
                       text-peach-500 dark:text-peach-400 rounded-2xl text-xs sm:text-sm font-light border border-peach-100/20 dark:border-peach-700/20 hover:border-peach-200/30 dark:hover:border-peach-600/30
                       transition-colors duration-300 focus:outline-none focus:ring-4 focus:ring-peach-100/30 dark:focus:ring-peach-700/30"
          >
            {content.ignoreText}
          </button>
        </div>
      </div>

      <style>{`
        @keyframes slideUp {
          from { 
            opacity: 0; 
            transform: translateY(20px); 
          }
          to { 
            opacity: 1; 
            transform: translateY(0); 
          }
        }
        
        @keyframes gentlePulse {
          0%, 100% { 
            opacity: 0.9; 
            transform: scale(1); 
          }
          50% { 
            opacity: 1; 
            transform: scale(1.02); 
          }
        }
      `}</style>
    </div>
  )
}

export default GentleBotPrompt